import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Crown, CheckCircle2 } from "lucide-react";

/**
 * Pre-launch VIP reservation modal — no payment collected. Captures name,
 * email, phone, ZIP so the founding $5/mo rate can be activated by link on
 * December 4, 2026 (public launch day).
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - source: string ("landing" | "offers-page" | "nav" | …)
 */
export default function PrelaunchVipModal({ open, onClose, source = "unknown" }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "", zip_code: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(null); // {already_registered, reserved_count}

    useEffect(() => {
        if (!open) {
            // Reset on close so the next open is a fresh form.
            setTimeout(() => {
                setForm({ name: "", email: "", phone: "", zip_code: "" });
                setDone(null);
                setSubmitting(false);
            }, 200);
        }
    }, [open]);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.zip_code.trim()) {
            toast.error("Please fill in every field.");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/prelaunch-vip/signup", { ...form, source });
            setDone(data);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save your reservation. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="sm:max-w-md" data-testid="prelaunch-vip-modal">
                {done ? (
                    <div className="text-center py-4" data-testid="prelaunch-vip-success">
                        <div className="mx-auto w-14 h-14 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                        </div>
                        <DialogHeader className="mt-4">
                            <DialogTitle className="font-heading text-2xl text-[#0A1128]">
                                {done.already_registered ? "You’re already on the list." : "You’re in."}
                            </DialogTitle>
                            <DialogDescription className="text-[#1A1D24]/70 mt-2">
                                On <span className="font-semibold text-[#0A1128]">December 4, 2026</span> we&rsquo;ll email you a
                                private activation link that locks in <span className="font-semibold text-[#9B1B30]">$5/mo VIP for life</span>.
                                Everything on ADN is free until then — keep browsing and redeeming.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="mt-4 text-xs uppercase tracking-widest text-[#1A1D24]/50">
                            {done.reserved_count?.toLocaleString?.() || done.reserved_count} founding members reserved so far
                        </div>
                        <Button
                            onClick={onClose}
                            className="mt-6 w-full bg-[#0A1128] hover:bg-[#1A264A] text-white rounded-sm"
                            data-testid="prelaunch-vip-close-success"
                        >
                            Continue browsing
                        </Button>
                    </div>
                ) : (
                    <form onSubmit={submit}>
                        <DialogHeader>
                            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center mb-3">
                                <Crown className="w-5 h-5 text-[#D4AF37]" />
                            </div>
                            <div className="eyebrow text-[#9B1B30]">Founding VIP · Pre-launch</div>
                            <DialogTitle className="font-heading text-2xl text-[#0A1128] mt-1">
                                Reserve $5/mo VIP for life
                            </DialogTitle>
                            <DialogDescription className="text-[#1A1D24]/70">
                                No payment now. On <span className="font-semibold text-[#0A1128]">December 4, 2026</span> we email
                                every pre-registered member a private activation link. Everything on ADN is free until then.
                            </DialogDescription>
                        </DialogHeader>

                        <div className="mt-5 space-y-3">
                            <div>
                                <Label htmlFor="pl-name">Full name</Label>
                                <Input
                                    id="pl-name"
                                    value={form.name}
                                    onChange={update("name")}
                                    placeholder="Jane Smith"
                                    required
                                    autoComplete="name"
                                    data-testid="prelaunch-vip-name"
                                />
                            </div>
                            <div>
                                <Label htmlFor="pl-email">Email</Label>
                                <Input
                                    id="pl-email"
                                    type="email"
                                    value={form.email}
                                    onChange={update("email")}
                                    placeholder="you@example.com"
                                    required
                                    autoComplete="email"
                                    data-testid="prelaunch-vip-email"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label htmlFor="pl-phone">Phone</Label>
                                    <Input
                                        id="pl-phone"
                                        type="tel"
                                        value={form.phone}
                                        onChange={update("phone")}
                                        placeholder="904-555-0134"
                                        required
                                        autoComplete="tel"
                                        data-testid="prelaunch-vip-phone"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="pl-zip">ZIP code</Label>
                                    <Input
                                        id="pl-zip"
                                        value={form.zip_code}
                                        onChange={update("zip_code")}
                                        placeholder="32202"
                                        required
                                        inputMode="numeric"
                                        autoComplete="postal-code"
                                        data-testid="prelaunch-vip-zip"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="mt-5 flex flex-col sm:flex-row gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={onClose}
                                className="sm:w-32 border-black/10"
                                data-testid="prelaunch-vip-cancel"
                            >
                                Not now
                            </Button>
                            <Button
                                type="submit"
                                disabled={submitting}
                                className="flex-1 bg-[#9B1B30] hover:bg-[#B8233C] text-white border-b-2 border-[#D4AF37] rounded-sm"
                                data-testid="prelaunch-vip-submit"
                            >
                                <Crown className="w-4 h-4 mr-2" />
                                {submitting ? "Reserving…" : "Reserve $5/mo for life"}
                            </Button>
                        </div>

                        <p className="mt-3 text-[11px] text-[#1A1D24]/55 leading-relaxed">
                            By reserving you agree to receive one launch-day email at your address above. We never sell
                            your info. Full privacy at <a href="/legal" className="underline">adn-local.com/legal</a>.
                        </p>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
